import { useAuthStore } from '~/stores/useAuthStore'
import { useInvocationLockStore } from '~/stores/useInvocationLockStore'
import { clearSaveSyncError, setSaveRecoverySyncing } from '~/composables/useGameSyncStatus'

/** Échecs consécutifs avant de lancer une récupération. */
const FAILURES_BEFORE_RECOVERY = 3
/** Resync serveur (loadGameState) avant d'envisager un reload. */
const MAX_RESYNC_ATTEMPTS = 2
/** Cooldown entre deux tentatives de récupération complètes. */
const RECOVERY_COOLDOWN_MS = 45_000
/** Délai minimum entre deux reload auto (évite boucle). */
const MIN_RELOAD_INTERVAL_MS = 120_000
/** Reload auto max par onglet / session. */
const MAX_AUTO_RELOADS_PER_SESSION = 2

const recovering = ref(false)
const needsManualRefresh = ref(false)
const lastRecoveryMessage = ref<string | null>(null)

let consecutiveSaveFailures = 0
let resyncAttemptsThisIncident = 0
let recoveryCooldownUntil = 0
let recoveryInFlight = false
let recoveryTimer: ReturnType<typeof setTimeout> | null = null
let autoReloadCount = 0
let lastAutoReloadAt = 0
let reloadScheduled = false

function canAutoReloadNow(): boolean {
  if (typeof window === 'undefined') return false
  if (reloadScheduled) return false
  if (autoReloadCount >= MAX_AUTO_RELOADS_PER_SESSION) return false
  if (Date.now() - lastAutoReloadAt < MIN_RELOAD_INTERVAL_MS) return false
  return true
}

function schedulePageReload(reason: string) {
  if (!canAutoReloadNow()) {
    needsManualRefresh.value = true
    lastRecoveryMessage.value = reason
    return
  }
  reloadScheduled = true
  autoReloadCount++
  lastAutoReloadAt = Date.now()
  console.warn('[SAVE-RECOVERY] Reload page:', reason)
  window.setTimeout(() => {
    window.location.reload()
  }, 400)
}

/**
 * Appelé après des sauvegardes ratées (hors 429 / 409 / keepalive / invocation).
 */
export function notifySaveFailure() {
  consecutiveSaveFailures++
  if (consecutiveSaveFailures < FAILURES_BEFORE_RECOVERY) return
  scheduleSaveRecovery('consecutive_save_failures')
}

export function notifySaveSuccess() {
  consecutiveSaveFailures = 0
  resyncAttemptsThisIncident = 0
  needsManualRefresh.value = false
  lastRecoveryMessage.value = null
  clearSaveSyncError()
}

export function scheduleSaveRecovery(trigger: string) {
  if (typeof window === 'undefined') return
  if (Date.now() < recoveryCooldownUntil) return
  if (recoveryInFlight) return

  const auth = useAuthStore()
  if (!auth.isAuthenticated) return

  const invocationLock = useInvocationLockStore()
  if (invocationLock.active) {
    console.warn('[SAVE-RECOVERY] Skipped — invocation lock active')
    return
  }

  if (recoveryTimer) clearTimeout(recoveryTimer)
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null
    void runSaveRecovery(trigger)
  }, 600)
}

async function runSaveRecovery(trigger: string) {
  if (recoveryInFlight) return
  if (Date.now() < recoveryCooldownUntil) return

  const auth = useAuthStore()
  if (!auth.isAuthenticated) return
  if (useInvocationLockStore().active) return

  recoveryInFlight = true
  recovering.value = true
  setSaveRecoverySyncing(true)
  needsManualRefresh.value = false

  try {
    console.warn(`[SAVE-RECOVERY] Start (${trigger}), resync attempt ${resyncAttemptsThisIncident + 1}`)

    await auth.loadGameState()

    if (auth.gameLoaded && !auth.gameLoadError) {
      console.warn('[SAVE-RECOVERY] Resync OK — état serveur réappliqué')
      notifySaveSuccess()
      return
    }

    resyncAttemptsThisIncident++

    if (resyncAttemptsThisIncident < MAX_RESYNC_ATTEMPTS) {
      recoveryCooldownUntil = Date.now() + 8_000
      scheduleSaveRecovery('resync_retry')
      return
    }

    if (canAutoReloadNow()) {
      schedulePageReload('resync_failed')
      return
    }

    needsManualRefresh.value = true
    lastRecoveryMessage.value = auth.gameLoadError ?? 'save_recovery_failed'
  } catch (e) {
    console.error('[SAVE-RECOVERY] Error:', e)
    resyncAttemptsThisIncident++

    if (resyncAttemptsThisIncident >= MAX_RESYNC_ATTEMPTS && canAutoReloadNow()) {
      schedulePageReload('recovery_exception')
    } else if (resyncAttemptsThisIncident < MAX_RESYNC_ATTEMPTS) {
      recoveryCooldownUntil = Date.now() + 8_000
      scheduleSaveRecovery('recovery_exception_retry')
    } else {
      needsManualRefresh.value = true
      lastRecoveryMessage.value = e instanceof Error ? e.message : String(e)
    }
  } finally {
    recoveryInFlight = false
    recovering.value = false
    setSaveRecoverySyncing(false)
    recoveryCooldownUntil = Date.now() + RECOVERY_COOLDOWN_MS
  }
}

export function manualSaveRecoveryReload() {
  if (typeof window === 'undefined') return
  reloadScheduled = true
  window.location.reload()
}

export function useSaveRecovery() {
  return {
    recovering: readonly(recovering),
    needsManualRefresh: readonly(needsManualRefresh),
    lastRecoveryMessage: readonly(lastRecoveryMessage),
    manualSaveRecoveryReload,
    scheduleSaveRecovery,
  }
}
